import { useState } from 'react';

import { ShiftDetailActivity } from '../api/types';

import Avatar from './Avatar';

// One row of the shift activity feed. Shared by the Shift Detail panel's
// Recent activity preview and the "View all" modal (GRAVY-10294) — the modal is
// opened from the preview, so a row that changed shape on the way in would read
// as a different event. Both surfaces render THIS component, which is what makes
// "apply to all recent/activity log UI" (GRAVY-10345) one change rather than two.

export const relativeDay = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const isSameDay = d.toDateString() === today.toDateString();
  if (isSameDay) return 'Today';
  const yest = new Date(today);
  yest.setDate(today.getDate() - 1);
  if (d.toDateString() === yest.toDateString()) return 'Yesterday';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(d);
};

/**
 * "Just now", "15 mins ago", "2 hrs ago", "8 hrs ago" — the design's wording
 * (GRAVY-10345), replacing the clock time GRAVY-10342 introduced.
 *
 * Both were attempts at the same requirement, "include a timestamp". A day
 * alone ("Today") orders nothing, which was the original complaint; a clock
 * time orders events but makes the reader do the arithmetic. Elapsed time
 * answers the question an Ops admin is actually asking — how long ago did this
 * happen — and still totally orders the feed.
 *
 * The exact moment stays in the `title` tooltip, so nothing is lost.
 */
export const elapsedLabel = (
  iso?: string | null,
  now: Date = new Date(),
): string => {
  if (!iso) return '';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';

  const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);
  // A clock skew between server and browser must not print "in 3 mins".
  if (seconds < 60) return 'Just now';

  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`;

  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? '' : 's'} ago`;

  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;

  // Past a week, elapsed time stops being useful — "37 days ago" is a date the
  // reader has to compute. Fall back to the calendar.
  return relativeDay(iso);
};

/** The full moment, for the tooltip — what the compact label leaves out. */
const exactTime = (iso?: string | null): string | undefined => {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(d);
};

/** How much of a message body shows before "show more". */
const QUOTE_PREVIEW = 60;

type Segment = NonNullable<ShiftDetailActivity['segments']>[number];

type Props = {
  item: ShiftDetailActivity;
  /** Opens a worker's profile — a linked name in the sentence. */
  onSelectWorker?: (workerId: number) => void;
  /** Opens the invited-worker list behind an "N workers" segment. */
  onSelectWorkers?: (workerIds: number[], names: string[]) => void;
};

/**
 * The sentence, rendered from the server's pieces.
 *
 * Every word comes from `segments`; this decides only how each piece LOOKS and
 * what it does. Where a segment needs a handler the caller did not supply, it
 * degrades to plain emphasis rather than a dead control.
 */
const Sentence = ({
  segments,
  onSelectWorker,
  onSelectWorkers,
}: {
  segments: Segment[];
  onSelectWorker?: (workerId: number) => void;
  onSelectWorkers?: (workerIds: number[], names: string[]) => void;
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <span className="dashv2-panel-acttext" data-testid="activitySentence">
      {segments.map((seg, i) => {
        // Segments have no ids — they are positional pieces of one sentence —
        // so the index IS the identity here.
        const key = `${seg.type}-${i}`;
        // Pieces are joined with a space, EXCEPT before punctuation: a list of
        // changed fields emits ", slots", and " , slots" is not a sentence.
        const space = i > 0 && !/^[,.;:!?]/.test(seg.text) ? ' ' : '';

        if (seg.type === 'actor') {
          return (
            <span key={key} className="dashv2-panel-actactor">
              {space}
              {seg.text}
            </span>
          );
        }

        if (seg.type === 'strong') {
          return (
            <span key={key} className="dashv2-panel-actstrong">
              {space}
              {seg.text}
            </span>
          );
        }

        if (seg.type === 'worker' && seg.worker_id != null && onSelectWorker) {
          return (
            <span key={key}>
              {space}
              <button
                type="button"
                className="dashv2-panel-actlink"
                data-testid="activityWorkerLink"
                onClick={() => onSelectWorker(seg.worker_id as number)}
              >
                {seg.text}
              </button>
            </span>
          );
        }

        if (
          seg.type === 'workers' &&
          seg.worker_ids?.length &&
          onSelectWorkers
        ) {
          return (
            <span key={key}>
              {space}
              <button
                type="button"
                className="dashv2-panel-actlink"
                data-testid="activityWorkersLink"
                onClick={() =>
                  onSelectWorkers(seg.worker_ids as number[], seg.names ?? [])
                }
              >
                {seg.text}
              </button>
            </span>
          );
        }

        if (seg.type === 'quote') {
          const long = seg.text.length > QUOTE_PREVIEW;
          const shown =
            long && !expanded
              ? `${seg.text.slice(0, QUOTE_PREVIEW)}…`
              : seg.text;
          return (
            <span key={key}>
              {space}
              <span
                className="dashv2-panel-actquote"
                data-testid="activityQuote"
              >
                “{shown}”
              </span>
              {long && (
                <>
                  {' '}
                  <button
                    type="button"
                    className="dashv2-panel-actmore"
                    data-testid="activityQuoteToggle"
                    aria-expanded={expanded}
                    onClick={() => setExpanded((open) => !open)}
                  >
                    {expanded ? 'show less' : 'show more'}
                  </button>
                </>
              )}
            </span>
          );
        }

        // `text`, and any segment whose interactive form is unavailable —
        // rendered as words rather than dropped, so the sentence stays whole.
        return (
          <span key={key}>
            {space}
            {seg.text}
          </span>
        );
      })}
    </span>
  );
};

const ActivityItem = ({ item, onSelectWorker, onSelectWorkers }: Props) => {
  const segments = item.segments ?? [];

  return (
    <div className="dashv2-panel-actitem">
      <div className="dashv2-panel-actrow">
        {/* WHOSE face belongs on the row (GRAVY-10350 A1/A2), in precedence:
              automated        → the GravyWork glyph
              an admin acted   → the ADMIN's photo
              otherwise        → the subject worker's

            Both halves were wrong. The glyph was keyed on
            `automated && !avatar_url`, and the payload carries the subject
            worker's photo on automated rows too — so a reminder sent by the
            system showed the recipient's face and the glyph never appeared. And
            an admin-driven row showed the worker the sentence is ABOUT rather
            than the person who acted: "Richard removed Marcus" showed Marcus. */}
        {item.automated ? (
          <span className="dashv2-panel-actbot" aria-hidden="true" />
        ) : (
          <Avatar
            name={item.actor_name || item.title}
            src={item.actor_avatar_url || item.avatar_url}
          />
        )}

        {segments.length > 0 ? (
          <Sentence
            segments={segments}
            onSelectWorker={onSelectWorker}
            onSelectWorkers={onSelectWorkers}
          />
        ) : (
          // No sentence defined for this event type — the flat form, which is
          // still every word the server resolved.
          <span className="dashv2-panel-acttext">
            {item.title || item.event_type}
            {item.detail ? ` — ${item.detail}` : ''}
          </span>
        )}
      </div>

      <div className="dashv2-panel-actmeta">
        <span title={exactTime(item.occurred_at)} data-testid="activityWhen">
          {elapsedLabel(item.occurred_at)}
        </span>
        <span className="dashv2-panel-dot" />
        <span>{item.event_type}</span>
      </div>
    </div>
  );
};

export default ActivityItem;
